import {
    Card,
    CardContent,
    List,
    ListItem,
    Typography,
    styled,
} from "@mui/material";
import { isNull, range } from "lodash";
import moment from "moment";
import { MouseEvent } from "react";
import Skeleton from "react-loading-skeleton";
import DownloadLocations from "../../../components/download-locations/download-locations";
import { displayDateFormat } from "../../../constants";
import {
    downloadListLocationsAction,
    downloadSubListLocationsAction,
} from "../../../features/download/redux/download.actions";
import { useAppSelector } from "../../../redux/store";
import { routeMapping } from "../../../route.config";
import { canAccessSourceUrl } from "../../../service/user-permission";
import { IWithClassName, IWithThemeProps } from "../../../theme/default.theme";
import { ListCategory } from "../../data-browser/enum/list-columns";
import CollapsibleList from "./collapsible-list";

const DetailsPane = ({ className }: IWithClassName) => {
    const { isLoading, details } = useAppSelector(
        ({ listPage }) => listPage.listDetails,
    );

    const sublist = useAppSelector(({ listPage }) => listPage.sublist.data);
    const role = useAppSelector(({ auth }) => auth.user?.role);
    const isEmptyParentList = isNull(details?.lastUpdated);

    const getDataBrowserLink = (type: string, value: string) =>
        `${routeMapping.dataBrowser.path}?${type}=${value}`;

    const handleDataBrowserClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const { href } = event.currentTarget;
        window.open(href, "_blank");
    };
    const getSublistCount = () =>
        sublist
            ? sublist
                  .filter((list) => list.hasAccess)
                  .reduce((total, list) => total + (list.records ?? 0), 0)
            : 0;
    const redText =
        details?.categories.indexOf(ListCategory.CATEGORY_CLOSED) !== -1
            ? " redText"
            : "";

    return (
        <div className={className}>
            <Card className="list-details-block">
                <CardContent>
                    <List>
                        {isLoading ? (
                            range(8).map((key) => (
                                <ListItem
                                    key={key}
                                    className="MuiListItem-root loading"
                                >
                                    <Skeleton />
                                </ListItem>
                            ))
                        ) : (
                            <>
                                <ListItem>
                                    <Typography className="list-header">
                                        List ID:
                                    </Typography>
                                    <Typography className="list-value">
                                        {details?.listId}
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography className="list-header">
                                        Name:
                                    </Typography>
                                    <Typography className="list-value">
                                        {details?.name}
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography className="list-header">
                                        Locations:
                                    </Typography>
                                    <div className="list-locations list-value">
                                        <Typography>
                                            {(details?.records ?? 0) +
                                                getSublistCount()}
                                        </Typography>
                                        {details && (
                                            <DownloadLocations
                                                label="Download"
                                                downloadAction={() =>
                                                    downloadSubListLocationsAction(
                                                        details.listId,
                                                    )
                                                }
                                            />
                                        )}
                                    </div>
                                </ListItem>
                                {!isEmptyParentList &&
                                sublist?.length &&
                                details?.records ? (
                                    <ListItem>
                                        <Typography className="list-header">
                                            Locations w/o sublists:
                                        </Typography>
                                        <div className="list-locations list-value">
                                            <Typography>
                                                {details.records}
                                            </Typography>
                                            <DownloadLocations
                                                label="Download"
                                                downloadAction={() =>
                                                    downloadListLocationsAction(
                                                        details.listId,
                                                    )
                                                }
                                            />
                                        </div>
                                    </ListItem>
                                ) : (
                                    <></>
                                )}
                                <ListItem>
                                    <Typography className="list-header">
                                        Last Updated:
                                    </Typography>
                                    <Typography className="list-value">
                                        {isEmptyParentList
                                            ? "Please see Sublists for last Updated Date."
                                            : details?.lastUpdated
                                              ? moment
                                                    .utc(details.lastUpdated)
                                                    .format(displayDateFormat)
                                              : "-"}
                                        {details?.covid && (
                                            <span className="covidText">
                                                COVID-19
                                            </span>
                                        )}
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography className="list-header">
                                        Countries:
                                    </Typography>
                                    <Typography className="list-value">
                                        <CollapsibleList
                                            items={
                                                (details?.countryCodes as Array<string>) ||
                                                []
                                            }
                                            displayItems={(
                                                (details?.countryCodes as Array<string>) ||
                                                []
                                            ).map(
                                                (code: string, index: number) =>
                                                    `${((details?.countryNames as Array<string>) || [])[index] || code} (${code})`,
                                            )}
                                            type="country"
                                            delimiter=" | "
                                            onClick={handleDataBrowserClick}
                                            getItemLink={getDataBrowserLink}
                                        />
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography className="list-header">
                                        Categories:
                                    </Typography>
                                    <Typography
                                        className={`list-value ${redText}`}
                                    >
                                        <CollapsibleList
                                            items={details?.categories}
                                            type="category"
                                            delimiter=" | "
                                            onClick={handleDataBrowserClick}
                                            getItemLink={getDataBrowserLink}
                                        />
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography className="list-header">
                                        SIC Code:
                                    </Typography>
                                    <Typography className="list-value">
                                        {details?.sic?.code ? (
                                            <a
                                                onClick={handleDataBrowserClick}
                                                href={getDataBrowserLink(
                                                    "sic",
                                                    details.sic.code,
                                                )}
                                            >
                                                {`${details.sic.code} - ${details.sic.name}`}
                                            </a>
                                        ) : (
                                            "-"
                                        )}
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography className="list-header">
                                        Primary NAICS Code:
                                    </Typography>
                                    <Typography className="list-value">
                                        {details?.naicsPrimary?.code ? (
                                            <a
                                                onClick={handleDataBrowserClick}
                                                href={getDataBrowserLink(
                                                    "naics",
                                                    details.naicsPrimary.code,
                                                )}
                                            >
                                                {`${details.naicsPrimary.code} - ${details.naicsPrimary.name}`}
                                            </a>
                                        ) : (
                                            "-"
                                        )}
                                    </Typography>
                                </ListItem>
                                {details?.naicsSecondary?.code && (
                                    <ListItem>
                                        <Typography className="list-header">
                                            Secondary NAICS Code:
                                        </Typography>
                                        <Typography className="list-value">
                                            <a
                                                onClick={handleDataBrowserClick}
                                                href={getDataBrowserLink(
                                                    "naics",
                                                    details.naicsSecondary.code,
                                                )}
                                            >
                                                {`${details.naicsSecondary.code} - ${details.naicsSecondary.name}`}
                                            </a>
                                        </Typography>
                                    </ListItem>
                                )}
                                <ListItem>
                                    <Typography className="list-header">
                                        Source URL:
                                    </Typography>
                                    <Typography className="list-value">
                                        {details?.sourceUrl &&
                                        canAccessSourceUrl(role) ? (
                                            <a
                                                href={`https://${details.sourceUrl}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {details.sourceUrl}
                                            </a>
                                        ) : (
                                            details?.sourceUrl
                                        )}
                                    </Typography>
                                </ListItem>
                                {details?.parentId && (
                                    <ListItem>
                                        <Typography className="list-header">
                                            Parent List:
                                        </Typography>
                                        <div className="list-locations list-value">
                                            <Typography>
                                                {details.parentName}
                                            </Typography>
                                            <DownloadLocations
                                                label="Download"
                                                downloadAction={() =>
                                                    downloadListLocationsAction(
                                                        details.parentId,
                                                    )
                                                }
                                            />
                                        </div>
                                    </ListItem>
                                )}
                            </>
                        )}
                    </List>
                </CardContent>
            </Card>
        </div>
    );
};

export default styled(DetailsPane)(({ theme }: IWithThemeProps) => ({
    ".list-details-block": {
        color: theme?.palette.detailsPane.textColor,
        fontSize: 16,
    },
    ".MuiListItem-root": {
        padding: theme?.spacing(4, 0),
        borderBottom: `1px solid ${
            theme?.palette.detailsPane.borderBottom ?? ""
        }  `,
        minHeight: 80,
        "&.loading": {
            display: "block",
            padding: theme?.spacing(4, 2),
        },
        a: {
            textDecoration: "none",
            color: theme?.palette.detailsPane.textColor,
        },
    },
    ".MuiListItem-root:last-child": {
        border: "none",
    },
    ".MuiCardHeader-root": {
        padding: theme?.spacing(0),
    },
    ".MuiCardContent-root": {
        padding: theme?.spacing(0),
    },
    ".MuiList-root": {
        padding: theme?.spacing(0),
    },
    ".list-header": {
        flexBasis: 280,
        padding: theme?.spacing(0, 5),
        fontWeight: 600,
        boxSizing: "border-box",
    },
    ".list-value": {
        display: "flex",
        flex: "1",
        flexWrap: "wrap",
        paddingRight: theme?.spacing(4),
        boxSizing: "border-box",
        span: {
            paddingRight: theme?.spacing(0.5),
        },
    },
    ".list-locations": {
        display: "flex",
        alignItems: "center",
        flexGrow: 1,
        justifyContent: "space-between",
    },
    ".covidText": {
        fontWeight: 600,
        color: theme?.palette.detailsPane.covidTextColor,
        paddingLeft: theme?.spacing(9),
    },
    ".redText a": {
        textTransform: "uppercase",
        fontWeight: 700,
        color: theme?.palette.listsTable.redTextColor,
    },
}));
